'use server'

import { apiRequest, ApiResponse } from '../request'
import { deliverPdf, downloadBlob, PdfDelivery } from '../../download-blob'
import type { CancelAppointmentResult } from '../exams/exam-api'
import type { PayoutShare } from '@/lib/billing/financials'
import { getStoredToken } from '@/lib/api/session-refresh'
import type { ServiceAddressSnapshot } from '@/lib/api/common/service-address'

export type ConsultationStatus =
    | 'REQUESTED'
    | 'SCHEDULED'
    | 'WAITING'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'NO_SHOW'

export type TranscriptionStatus =
    | 'PENDING'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'FAILED'

/**
 * Hipótese diagnóstica com o raciocínio explícito — é o que permite ao médico
 * discordar com base em algo, em vez de aceitar ou ignorar no escuro.
 */
export interface DiagnosticHypothesis {
    description: string
    /** CID-10 validado contra a tabela (a IA busca por ferramenta, não escreve de memória). */
    icdCode?: string
    icdDescription?: string
    icdCodeId?: number
    likelihood: 'alta' | 'media' | 'baixa'
    supportingFindings: string[]
    againstFindings?: string[]
}

/**
 * Exame sugerido para a investigação. Com `catalogId`, o pedido marca o item sem
 * adivinhar por texto; sem ele, o exame não existe no catálogo da conta.
 */
export interface SuggestedExam {
    name: string
    catalogId?: number
    catalogName?: string
    /** Por que este exame ajuda NESTE caso. */
    rationale: string
    priority: 'urgente' | 'rotina'
    relatedHypothesis?: string
}

/** Sinal de alarme: o que não pode esperar a próxima consulta. */
export interface RedFlag {
    description: string
    severity: 'alta' | 'media'
    recommendedAction: string
}

export interface ConsultationAiExtractedData {
    mainComplaints: string[]
    symptoms: string[]
    physicalExamFindings: string[]
    medicationsPrescribed: string[]
    examsRequested: string[]
    orders: string[]
    allergiesMentioned: string[]
    riskFactors: string[]
    patientQuestions: string[]
    doctorInstructions: string[]

    // Interpretação clínica. Opcionais: análises geradas antes desta versão não as
    // têm, e a tela precisa continuar abrindo.
    diagnosticHypotheses?: DiagnosticHypothesis[]
    suggestedExams?: SuggestedExam[]
    redFlags?: RedFlag[]
}

export interface ConsultationAiAnalysis {
    summary: string
    diagnosis?: string
    recommendations?: string
    followUp?: string
    extractedData: ConsultationAiExtractedData
    confidenceScore: number
    warnings: string[]
}

export interface ConsultationAttachment {
    fileName: string
    filePath: string
    fileType: string
    fileSize: number
    description?: string
    userName?: string
    uploadedAt: string
}

export interface Consultation extends ServiceAddressSnapshot {
    id: number
    title?: string
    status: ConsultationStatus
    scheduledAt?: string
    startedAt?: string
    completedAt?: string
    /** Cancelamento: o registro não é excluído, fica listado como Cancelado. */
    cancelledAt?: string | null
    cancellationReason?: string | null
    duration?: number
    isReturn?: boolean
    /** Vencimento da guia renovado manualmente pelo operador ("Renovar Guia"). */
    guideValidUntil?: string | null

    transcription?: string
    transcriptionStatus: TranscriptionStatus
    transcribedAt?: string

    aiSummary?: string
    aiDiagnosis?: string
    aiRecommendations?: string
    aiFollowUp?: string
    aiExtractedData?: ConsultationAiExtractedData
    aiConfidenceScore?: number
    aiModel?: string
    aiProcessedAt?: string

    audioUrl?: string
    audioDuration?: number
    audioFormat?: string
    audioSize?: number

    notes?: string
    diagnosis?: string
    treatmentPlan?: string

    vitalSigns?: any

    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
        email?: string
        whatsAppNumber?: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
        email?: string
        registrationNumber?: string
        councilName?: string
    }
    createdById?: number
    createdBy?: {
        id: number
        name: string
        email?: string
    }
    companyId: number
    company?: {
        id: number
        name: string
        tradingName?: string
    }
    triageId?: number
    triage?: {
        id: number
        urgency: string
        chiefComplaint?: string
        symptoms?: string
    }
    partnerId?: number
    partner?: {
        id: number
        name: string
        tradingName?: string
    }
    healthPlanId?: number
    healthPlan?: { id: number; name: string; ansCode?: string }
    hospitalId?: number | null
    hospital?: {
        id: number
        name: string
        tradingName?: string | null
        cnpj?: string | null
    } | null
    discount?: number

    attachments?: ConsultationAttachment[]
    metadata?: Record<string, any>

    catalogId?: number
    catalog?: {
        id: number
        name: string
        price?: number
        sellingPrice?: number
        professional?: { id: number; name: string }
        specialty?: { id: number; name: string }
        specialties?: { specialty: { id: number; name: string } }[]
    }
    variations?: {
        variationId: number
        variationName?: string | null
        quantity: number
        unitPrice: number
    }[]

    dealId?: number
    deal?: {
        id: number
        title: string
        status: string
        totalValue?: number
        stage: { id: number; name: string; color?: string }
        pipeline: { id: number; name: string; type: string }
        lead: { id: number; name: string; phoneNumber?: string; email?: string }
    }

    billingId?: number | null
    billing?: {
        id: number
        status: string
        amount: number
        paidAmount?: number | null
        paymentMethod?: string | null
        dueDate: string
        paidAt?: string | null
        publicToken?: string | null
        description?: string | null
        pixQrCodeBase64?: string | null
        pixCopyPaste?: string | null
        bankSlipUrl?: string | null
        barcode?: string | null
        digitableLine?: string | null
        paymentLink?: string | null
        /** Cobrança gerada no gateway: habilita a consulta ao provedor antes da baixa. */
        providerPaymentId?: string | null
        refundedAmount?: number | null
        refundedAt?: string | null
        refundedVia?: string | null
        /**
         * Rateio do repasse por atendimento. Presente quando a cobrança cobre mais
         * de um (o paciente pagou a visita inteira de uma vez): aí os valores desta
         * tela têm de sair da FATIA, não da cobrança, que vale pelos outros também.
         * Ver docs/billing-payout-share.md.
         */
        payoutShares?: PayoutShare[] | null
    } | null

    createdAt: string
    updatedAt: string
}

export interface CreateConsultationData {
    /**
     * Local de atendimento confirmado no agendamento (snapshot de auditoria).
     * Sem isso, o backend deriva de parceiro > profissional.
     */
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED'
    title?: string
    status?: ConsultationStatus
    scheduledAt?: string
    notes?: string
    diagnosis?: string
    treatmentPlan?: string
    vitalSigns?: any
    isReturn?: boolean
    clientId: number
    professionalId?: number
    partnerId?: number
    healthPlanId?: number
    hospitalId?: number
    /**
     * Cobrança em aberto do paciente onde SOMAR esta consulta, em vez de gerar
     * cobrança própria — quem agenda em vários lugares paga uma vez só.
     */
    billingTargetId?: number
    companyId: number
    triageId?: number
    agendaId?: number
    roomId?: number
    duration?: number
    discount?: number
    /** Preço FINAL definido à mão pelo operador (centavos). O desconto não é reaplicado sobre ele. */
    overridePrice?: number
    paymentMethod?: string
    /** Parcelas no cartão combinadas com o pagador (trava o checkout). */
    installmentCount?: number
    /**
     * Agendar sem gerar cobrança agora — o paciente paga no dia, na recepção.
     *
     * O valor do atendimento continua gravado; só a emissão espera, e sai depois pelo
     * "Gerar cobrança" da tela de detalhe. Não confundir com o orçamento, que ainda não
     * foi aprovado, nem com a forma "decidir na tela de pagamento", que EMITE a cobrança
     * e só deixa a forma em aberto.
     */
    deferBilling?: boolean
    catalogId?: number
    variations?: { catalogId: number; quantity: number }[]
    pipelineId?: number
    metadata?: any
    attachments?: any
    /** Por ordem de chegada: tem data, não tem hora marcada. */
    arrivalOrder?: boolean
}

export interface UpdateConsultationData
    extends Partial<
        Omit<
            CreateConsultationData,
            | 'clientId'
            | 'professionalId'
            | 'partnerId'
            | 'companyId'
            | 'healthPlanId'
        >
    > {
    /**
     * `null` RETIRA o executante. Sem o null, o campo ausente significa "não mexer" e
     * o profissional removido na tela continuava gravado.
     */
    professionalId?: number | null
    partnerId?: number | null
    /** `null` RETIRA o convênio. Ausente significa "não mexer neste campo". */
    healthPlanId?: number | null
    /**
     * Cria a cobrança do atendimento quando ele ainda não tem uma. Enviado só quando o
     * operador mexe no bloco de pagamento — editar apenas a data não pode gerar cobrança.
     */
    generateBilling?: boolean
    startedAt?: string
    completedAt?: string
    duration?: number
    transcription?: string
    transcriptionStatus?: TranscriptionStatus
    aiSummary?: string
    aiDiagnosis?: string
    aiRecommendations?: string
    aiFollowUp?: string
    aiExtractedData?: any
}

// === CRUD FUNCTIONS ===

export async function getConsultations(
    companyId?: number
): Promise<ApiResponse<Consultation[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<Consultation[]>('consultations', { params })
}

export async function getConsultationsByCompany(
    companyId: number,
    params?: { page?: number; limit?: number; search?: string; status?: string; partnerId?: number; id?: number; cpf?: string; clientId?: number; createdById?: number; startDate?: string; endDate?: string; sortBy?: string; sortOrder?: string;
        /**
         * Janela de DATA DE PAGAMENTO (`billing.paidAt`), independente de
         * `startDate`/`endDate`, que filtram o registro. É o que o fechamento de caixa
         * usa: a venda de sexta paga na segunda pertence ao caixa da segunda.
         */
        paidStartDate?: string; paidEndDate?: string;
        /** Status da cobrança vinculada; 'NONE' = sem cobrança. */
        billingStatus?: string;
    }
): Promise<ApiResponse<Consultation[]>> {
    return apiRequest<Consultation[]>(`consultations/company/${companyId}`, { params: params || {} })
}

export async function getConsultationsByClient(
    clientId: number
): Promise<ApiResponse<Consultation[]>> {
    return apiRequest<Consultation[]>(`consultations/client/${clientId}`, {})
}

export async function getConsultationsByProfessional(
    professionalId: number
): Promise<ApiResponse<Consultation[]>> {
    return apiRequest<Consultation[]>(
        `consultations/professional/${professionalId}`,
        {}
    )
}

export async function getConsultationsToday(
    companyId: number
): Promise<ApiResponse<Consultation[]>> {
    return apiRequest<Consultation[]>(`consultations/today/${companyId}`, {})
}

export async function getConsultationsByStatus(
    companyId: number,
    status: ConsultationStatus
): Promise<ApiResponse<Consultation[]>> {
    return apiRequest<Consultation[]>(`consultations/status/${status}`, {
        params: { companyId },
    })
}

export async function getConsultation(
    id: number
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}`, {})
}

export async function createConsultation(
    data: CreateConsultationData
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>('consultations', { data }, 'post')
}

export async function updateConsultation(
    id: number,
    data: UpdateConsultationData
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}`, { data }, 'patch')
}

/**
 * Troca o local de atendimento já gravado na consulta.
 *
 * Endpoint próprio, e não o `updateConsultation`: a edição arrasta recálculo de
 * preço e cobrança complementar, que não têm nada a ver com corrigir uma rua.
 */
export async function updateConsultationServiceAddress(
    id: number,
    data: ServiceAddressSnapshot
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(
        `consultations/${id}/service-address`,
        { data },
        'patch'
    )
}

export async function updateConsultationStatus(
    id: number,
    status: ConsultationStatus
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(
        `consultations/${id}/status`,
        { data: { status } },
        'patch'
    )
}

/** Renova a validade da guia: vencimento = hoje + dias configurados na empresa. */
export async function renewConsultationGuide(
    id: number
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}/renew-guide`, {}, 'patch')
}

export async function deleteConsultation(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consultations/${id}`, {}, 'delete')
}

// === AUDIO FUNCTIONS ===

export async function uploadConsultationAudio(
    id: number,
    audioBlob: Blob
): Promise<ApiResponse<Consultation>> {
    const formData = new FormData()
    formData.append('file', audioBlob, 'audio.webm')
    return apiRequest<Consultation>(
        `consultations/${id}/audio`,
        { data: formData, formData: true },
        'post'
    )
}

export async function getConsultationAudio(
    id: number
): Promise<
    ApiResponse<{
        url: string
        duration?: number
        format?: string
        size?: number
    }>
> {
    return apiRequest(`consultations/${id}/audio`, {})
}

export async function deleteConsultationAudio(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consultations/${id}/audio`, {}, 'delete')
}

// === TRANSCRIPTION FUNCTIONS ===

export async function transcribeConsultation(
    id: number
): Promise<ApiResponse<{ transcription: string }>> {
    return apiRequest<{ transcription: string }>(
        `consultations/${id}/transcribe`,
        {},
        'post'
    )
}

export async function getConsultationTranscription(
    id: number
): Promise<
    ApiResponse<{
        transcription?: string
        status: TranscriptionStatus
        transcribedAt?: string
    }>
> {
    return apiRequest(`consultations/${id}/transcription`, {})
}

// === AI ANALYSIS FUNCTIONS ===

/**
 * A análise vive em `medical/consultation-analysis`, e não na rota da consulta,
 * porque lê o PRONTUÁRIO do paciente (alergia, comorbidade, medicação em uso, Mapa
 * da Saúde) — e `bookings` não pode importar `medical` no backend. A LEITURA do
 * resultado continua em `consultations/:id/ai-summary`: são campos da própria
 * consulta.
 */
export async function analyzeConsultation(
    id: number
): Promise<ApiResponse<ConsultationAiAnalysis>> {
    return apiRequest<ConsultationAiAnalysis>(
        `medical/consultation-analysis/${id}`,
        {},
        'post'
    )
}

export async function getConsultationAiSummary(
    id: number
): Promise<
    ApiResponse<{
        summary?: string
        diagnosis?: string
        recommendations?: string
        followUp?: string
        extractedData?: ConsultationAiExtractedData
        confidenceScore?: number
        model?: string
        processedAt?: string
    }>
> {
    return apiRequest(`consultations/${id}/ai-summary`, {})
}

export async function regenerateConsultationSummary(
    id: number
): Promise<ApiResponse<ConsultationAiAnalysis>> {
    return apiRequest<ConsultationAiAnalysis>(
        `medical/consultation-analysis/${id}/regenerate`,
        {},
        'post'
    )
}

// === ATTACHMENT FUNCTIONS ===

export async function uploadConsultationAttachment(
    id: number,
    file: File,
    description?: string
): Promise<ApiResponse<Consultation>> {
    const formData = new FormData()
    formData.append('file', file)
    if (description) {
        formData.append('description', description)
    }
    return apiRequest<Consultation>(
        `consultations/${id}/attachments`,
        { data: formData, formData: true },
        'post'
    )
}

export async function deleteConsultationAttachment(
    id: number,
    index: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `consultations/${id}/attachments/${index}`,
        {},
        'delete'
    )
}

// === PDF FUNCTIONS ===

/** Envia o registro da consulta ao paciente por e-mail. */
export async function sendConsultationEmail(
    consultationId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consultations/${consultationId}/send-email`, {}, 'post')
}

export async function printConsultation(
    consultationId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/consultations/${consultationId}/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar consulta em PDF')

    const blob = await response.blob()
    const filename = `consulta-${consultationId}.pdf`

    deliverPdf(blob, filename, delivery)
}

// === BILLING FUNCTIONS ===

export async function markConsultationBillingAsPaid(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consultations/${id}/billing/mark-paid`, {}, 'patch')
}

export async function cancelConsultationBilling(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consultations/${id}/billing/cancel`, {}, 'patch')
}

/**
 * Cancela a consulta sem excluí-la: ela continua na listagem com status
 * "Cancelada". A cobrança sem pagamento é cancelada junto; a cobrança paga vira
 * solicitação de estorno (`refundPending: true`).
 */
export async function cancelConsultation(
    id: number,
    reason?: string,
): Promise<ApiResponse<CancelAppointmentResult>> {
    return apiRequest<CancelAppointmentResult>(
        `consultations/${id}/cancel`,
        { data: { reason } },
        'patch',
    )
}

/** Transfere o vendedor (createdBy) da consulta para outro usuário. */
export async function transferConsultationSeller(
    id: number,
    sellerId: number,
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(
        `consultations/${id}/transfer-seller`,
        { data: { sellerId } },
        'patch',
    )
}

/** Gera a cobrança de uma consulta que ficou sem cobrança. Retorna o id da cobrança criada. */
export async function generateConsultationBilling(
    id: number,
    /**
     * Quanto cobrar agora, em centavos. Ausente = o saldo inteiro.
     *
     * Vai no gesto de CRIAR porque alterar o valor de uma cobrança atrelada a
     * atendimento é proibido — é por ali que sairia desconto sem autorização.
     */
    amount?: number,
): Promise<ApiResponse<{ billingId: number }>> {
    return apiRequest<{ billingId: number }>(
        `consultations/${id}/generate-billing`,
        amount != null ? { data: { amount } } : {},
        'post',
    )
}

// === LABELS AND COLORS ===

export const CONSULTATION_STATUS_LABELS: Record<ConsultationStatus, string> = {
    REQUESTED: 'Solicitada',
    SCHEDULED: 'Agendada',
    WAITING: 'Aguardando',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    NO_SHOW: 'Não Compareceu',
}

export const CONSULTATION_STATUS_COLORS: Record<ConsultationStatus, string> = {
    REQUESTED: 'badge-warning',
    SCHEDULED: 'badge-info',
    WAITING: 'badge-warning',
    IN_PROGRESS: 'badge-secondary',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
    NO_SHOW: 'badge-ghost',
}

/**
 * Sem data marcada a consulta ainda é uma solicitação, mesmo com o status
 * `SCHEDULED`; e uma solicitação com data já pode se apresentar como agendada.
 */
export function getConsultationStatusLabel(
    status: ConsultationStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'SCHEDULED' && !scheduledAt) return 'Solicitada'
    if (status === 'REQUESTED' && scheduledAt) return 'Agendada'
    return CONSULTATION_STATUS_LABELS[status]
}

export function getConsultationStatusColor(
    status: ConsultationStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'SCHEDULED' && !scheduledAt) return 'badge-warning'
    if (status === 'REQUESTED' && scheduledAt) return 'badge-info'
    return CONSULTATION_STATUS_COLORS[status]
}

export const TRANSCRIPTION_STATUS_LABELS: Record<TranscriptionStatus, string> =
    {
        PENDING: 'Pendente',
        PROCESSING: 'Processando',
        COMPLETED: 'Concluida',
        FAILED: 'Falhou',
    }

export const TRANSCRIPTION_STATUS_COLORS: Record<TranscriptionStatus, string> =
    {
        PENDING: 'badge-ghost',
        PROCESSING: 'badge-warning',
        COMPLETED: 'badge-success',
        FAILED: 'badge-error',
    }

export interface RescheduleAppointmentData {
    /** ISO. Com `arrivalOrder`, a hora é descartada no backend. */
    scheduledAt: string
    /** Por ordem de chegada: tem data, não tem hora. */
    arrivalOrder?: boolean
    reason?: string
}

/**
 * Remarca o atendimento: move a entidade E a agenda juntas, valida conflito de horário
 * (empresas de INTERMEDIAÇÃO não bloqueiam), grava o histórico e avisa o paciente da nova
 * data.
 *
 * Diferente de um `update` com data nova — que só sobrescrevia o campo, sem checar
 * conflito e sem avisar ninguém.
 */
export async function rescheduleConsultation(
    id: number,
    data: RescheduleAppointmentData
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}/reschedule`, { data }, 'patch')
}

/**
 * REMOVE a data do atendimento: o horário é liberado (o `AgendaEvent` é apagado) e o
 * status acompanha.
 *
 * A consulta continua existindo — some da agenda e volta para SOLICITADA. Não
 * confundir com cancelar: cobrança, itens e repasses ficam intactos.
 */
export async function unscheduleConsultation(
    id: number,
    data: { reason?: string } = {}
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}/unschedule`, { data }, 'patch')
}

/**
 * Troca o status manualmente — a régua do operador sobre o que de fato aconteceu.
 * Só o status (e o evento da agenda) mudam: nada de cobrança, repasse ou aviso ao
 * paciente. `CANCELLED` é recusado pelo backend; cancelar tem ação própria.
 */
export async function changeConsultationStatus(
    id: number,
    status: ConsultationStatus,
): Promise<ApiResponse<Consultation>> {
    return apiRequest<Consultation>(`consultations/${id}/status`, { data: { status } }, 'patch')
}
